import { useMemo } from 'react';
import * as zod from 'zod';

import type { IconKind } from '@sb/design-system/icons';
import { currentShowPathThumbnail } from '@sb/feathers-types';
import type { SkillData } from '@sb/feathers-types';
import {
  subscribeToSkills,
  useSkillsStore,
} from '@sbrc/services/feathers-client/skills/skillsStore';

import type { SkillCardSkill } from './RedesignSkillCard';

/** @feature-flag enableSkills */

// Re-exported for callers that resolve a skill from outside React render. The
// store + accessor live in the services layer so `@sbrc/services` never imports
// `@sbrc/components` (which would create a services <-> components cycle).
export { getLocateSkillByIdFromStore } from '@sbrc/services/feathers-client/skills/skillsStore';

export const SKILL_TYPE_DISPLAY: Record<
  SkillData['skillType'],
  SkillCardSkill['type']
> = {
  control: 'Positioning',
  locate: 'Locate',
  classify: 'Classify',
  agent: 'Agent',
  'ai-motion': 'ShowPath',
  inspect: 'Inspect',
};

export const SKILL_TYPE_ICON: Record<SkillData['skillType'], IconKind> = {
  control: 'settings',
  locate: 'scope',
  classify: 'squareGrid2x2',
  agent: 'sparkle',
  'ai-motion': 'tooltip',
  inspect: 'eye',
};

export const SKILL_TYPE_BG: Record<SkillData['skillType'], string> = {
  control: 'tw-bg-blue',
  locate: 'tw-bg-orange',
  classify: 'tw-bg-green',
  agent: 'tw-bg-orange',
  'ai-motion': 'tw-bg-purple-60',
  inspect: 'tw-bg-green',
};

// List cards use a neutral chip per the 2026 prototype; the detail-view
// sidebar keeps the per-type SKILL_TYPE_BG accent.
export const SKILL_CARD_CHIP_BG = 'tw-bg-gray-60';

/**
 * Resolve the icon to display for a skill: a custom icon persisted on
 * `skill.icon` when present, otherwise the skill-type default from
 * `SKILL_TYPE_ICON`. Centralises the `as IconKind` cast in one place.
 */
export function getSkillIcon(skill: SkillData): IconKind {
  return typeof skill.icon === 'string'
    ? (skill.icon as IconKind)
    : SKILL_TYPE_ICON[skill.skillType];
}

const TaughtSnapshot = zod.object({ snapshotStorageID: zod.string() });

/**
 * The card's header image: the frame the skill was taught on, or for a
 * ShowPath skill, which has none, a drawing of the path it plays.
 */
function cardImageStorageID(skill: SkillData): string | undefined {
  if (skill.skillType === 'ai-motion') {
    return currentShowPathThumbnail(skill.shared)?.storageID ?? undefined;
  }

  const snapshot = TaughtSnapshot.safeParse(skill.shared ?? {});

  return snapshot.success ? snapshot.data.snapshotStorageID : undefined;
}

function convertSkillToCardSkill(skill: SkillData): SkillCardSkill {
  return {
    id: skill.id,
    name: skill.name,
    type: SKILL_TYPE_DISPLAY[skill.skillType],
    iconKind: getSkillIcon(skill),
    iconColor: 'tw-text-white',
    iconBg: SKILL_CARD_CHIP_BG,
    createdAt: skill.createdAt ? new Date(skill.createdAt) : undefined,
    updatedAt: skill.updatedAt ? new Date(skill.updatedAt) : undefined,
    // Server-side wizard draft: tagged as such in the grid, and excluded
    // everywhere else until finalized.
    isDraft: skill.isDraft,
    snapshotStorageID: cardImageStorageID(skill),
  };
}

export function useSkills(options?: {
  enabled?: boolean;
  /**
   * Include in-progress wizard drafts (`isDraft` rows). Default off. A skill
   * picker that opts in must pass its list through `useStickyDraftSkills`, so
   * it offers only the draft its step or anchor already names.
   */
  includeDrafts?: boolean;
  /**
   * Include agent skills. Only the Skills tab opts out — agent skills are
   * managed in Settings → Agent and have no card there.
   * It must stay opt-out rather than the default: the server enforces name
   * uniqueness across every skill type (`checkDuplicateSkillName`), so callers
   * that pre-check a typed name against this list — notably
   * `SkillCreationWizard` — need agent names in it or they green-light a name
   * the save then rejects.
   */
  includeAgentSkills?: boolean;
}): {
  skills: SkillCardSkill[];
  isLoading: boolean;
} {
  const enabled = options?.enabled ?? true;
  const includeDrafts = options?.includeDrafts ?? false;
  const includeAgentSkills = options?.includeAgentSkills ?? true;

  if (enabled) {
    subscribeToSkills();
  }

  const rawSkills = useSkillsStore((state) => state.rawSkills);
  const isLoading = useSkillsStore((state) => state.isLoading);

  // Derive the card view-models from raw skills. `rawSkills` keeps a stable
  // reference while unchanged (see the store), so this memo only recomputes
  // when the skill set actually changes.
  const skills = useMemo(
    () =>
      (includeDrafts ? rawSkills : rawSkills.filter((s) => !s.isDraft))
        .filter((s) => includeAgentSkills || s.skillType !== 'agent')
        .map(convertSkillToCardSkill),
    [rawSkills, includeDrafts, includeAgentSkills],
  );

  return { skills, isLoading };
}

export function useRawSkills(options?: {
  enabled?: boolean;
  /** See useSkills — default excludes wizard drafts. */
  includeDrafts?: boolean;
}): SkillData[] {
  const enabled = options?.enabled ?? true;
  const includeDrafts = options?.includeDrafts ?? false;

  if (enabled) {
    subscribeToSkills();
  }

  const rawSkills = useSkillsStore((state) => state.rawSkills);

  return useMemo(
    () => (includeDrafts ? rawSkills : rawSkills.filter((s) => !s.isDraft)),
    [rawSkills, includeDrafts],
  );
}

/**
 * The robot's skills as routine validation reads them: undefined until they
 * have loaded, so a step is not flagged for a skill that has not arrived yet,
 * and drafts included, so a step on a draft is told what the draft still needs.
 */
export function useValidationSkills(): SkillData[] | undefined {
  const skills = useRawSkills({ includeDrafts: true });
  const isLoading = useSkillsStore((state) => state.isLoading);

  return isLoading ? undefined : skills;
}

/**
 * Copy for the naming-step guard below. Matches the server's rejection text
 * (`checkDuplicateSkillName`) so the inline message and the fallback toast read
 * the same.
 */
export const SKILL_NAME_TAKEN_MESSAGE =
  'A skill with this name already exists.';

/**
 * Trimmed names already owned by finalized skills, so a wizard can reject a
 * duplicate name at the naming step instead of letting the operator reach the
 * end and fail on save.
 *
 * Two details keep this in lockstep with the server's `checkDuplicateSkillName`:
 * drafts are excluded (they don't reserve names, per the draft short-circuit in
 * that hook), and the set holds exact trimmed names because the query compares
 * with SQL `=` — case-sensitively. Matching that exactly means the UI never
 * blocks a name the server would accept. The server check remains the
 * authority: it still catches a name another operator claimed mid-wizard.
 */
export function useTakenSkillNames(options?: {
  enabled?: boolean;
}): ReadonlySet<string> {
  const skills = useRawSkills(options);

  return useMemo(() => new Set(skills.map((s) => s.name.trim())), [skills]);
}

export function useRawSkill(skillId: string): {
  skill: SkillData | undefined;
  isLoading: boolean;
} {
  subscribeToSkills();

  const skill = useSkillsStore((state) =>
    state.rawSkills.find((s) => s.id === skillId),
  );

  const isLoading = useSkillsStore((state) => state.isLoading);

  return { skill, isLoading };
}
